import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;
import { fetchAllPages } from '../../services/fetchAllPages.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Car {
  _id: string;
  name?: string;
}
interface MaintenanceRow {
  _id: string;
  car_id: string;
  title: string;
  maintenance_type: string;
  status: string;
  start_at?: string;
  end_at?: string;
}

export function MaintenanceListPage() {
  const ability = useAbility();
  const canWrite = ability.can('create', 'MaintenanceRecord');
  const [carId, setCarId] = useState('');

  const { data: cars } = useQuery({
    queryKey: RESOURCE_KEYS.car.list,
    // 162 (US3): every car, not the first page of 20. `/cars` paginates with a default of 20 and
    // a maximum of 100, and this control read it with no page parameter at all — so on a fleet
    // over 20 cars the car you wanted was simply not in the list, with no error and no note.
    queryFn: () => fetchAllPages<Car>('/cars', (url) => api.get(url)),
  });
  const carName = new Map((cars ?? []).map((c) => [c._id, c.name ?? c._id]));

  // 162 (#21): a pager and a search box. `resetOn` carries the car filter (FR-007).
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [carId],
  });

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (carId) params.set('car_id', carId);
    // Only when there is something to filter by (FR-004).
    if (search) params.set('q', search);
    return `/maintenance-records?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.maintenance.list, carId, page, search],
    queryFn: () => api.get<{ items: MaintenanceRow[]; pages: number }>(listPath),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/maintenance-records/${id}`),
    invalidateKey: RESOURCE_KEYS.maintenance.list,
  });

  const columns: Column<MaintenanceRow>[] = [
    { key: 'title', header: 'Title' },
    { key: 'car_id', header: 'Car', render: (r) => carName.get(r.car_id) ?? r.car_id },
    { key: 'maintenance_type', header: 'Type' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: '_id',
      header: 'Actions',
      render: (r) => (
        <RowActions
          actions={[
            canWrite && {
              key: 'edit',
              label: 'Edit',
              href: `/maintenance/${r._id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            canWrite && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger',
              confirmMessage: `Delete maintenance record "${r.title}"? Its car block is released.`,
              onClick: () => remove.run(r._id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Maintenance"
        description="Service records; an active window blocks the car from bookings."
        actions={
          canWrite ? (
            <Link to="/maintenance/new">
              <Button>+ New</Button>
            </Link>
          ) : undefined
        }
      />
      <Card className="mb-4 max-w-xs">
        <Select
          id="car_filter"
          aria-label="Car"
          data-testid="maintenance-car-filter"
          value={carId}
          onChange={(e) => setCarId((e.target as HTMLSelectElement).value)}
        >
          <option value="">All cars</option>
          {cars?.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name ?? c._id}
            </option>
          ))}
        </Select>
      </Card>
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search maintenance records by title"
        testId="maintenance-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        emptyMessage="No maintenance records"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
